import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CompanyService } from '@app/core';
import {
  ApiOverwrite,
  DataSource,
  ProviderSource,
  SOURCE_ABBREVIATIONS,
  ValueSource,
} from '@app/model';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { OverlayPanelComponent } from '../overlay-panel/overlay-panel.component';
import { SourceDisplayService } from './source-display.service';

/** The details of a manual change for the popover. */
interface ManualChange {
  user: string;
  date: string | null;
  original: unknown;
}

/**
 * The source of a value as a small badge next to it (CB, SS, TX, M for a
 * manual change, Σ for a value Hunter computes) with a popover: the exact
 * source, the other providers of the company, the data date and, for manual
 * changes, who changed it when and the original value. Without the exact
 * source the badge shows the providers of the row as outlines. Hidden when
 * "Show sources" is off.
 */
@Component({
  selector: 'app-source-badge',
  templateUrl: './source-badge.component.html',
  styleUrls: ['./source-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SourceBadgeComponent {
  private companyService = inject(CompanyService);
  private changeDetector = inject(ChangeDetectorRef);
  public readonly display = inject(SourceDisplayService);

  @Input() source: ValueSource | null = null;
  /** The name of the value for the popover, a translation key or text. */
  @Input() label: string | null = null;
  /** The user may edit the value: a manual change links to the editor. */
  @Input() editable = false;
  /** "Edit data" in the popover of a manual change. */
  @Output() editData = new EventEmitter<void>();

  @ViewChild('panel') panel?: OverlayPanelComponent;

  public readonly abbreviations = SOURCE_ABBREVIATIONS;
  public readonly lastUpdate$ = this.companyService.lastUpdate$;
  public manual$: Observable<ManualChange | null> | null = null;
  private pinned = false;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  /** The providers shown as outlines when the exact source is unknown. */
  get outline(): ProviderSource[] {
    return this.source && !this.source.exact ? this.source.available : [];
  }

  /** The other providers of the row, besides the exact source. */
  get others(): ProviderSource[] {
    return (this.source?.available ?? []).filter(
      (x) => x !== this.source?.exact
    );
  }

  get visible(): boolean {
    return !!this.source && (!!this.source.exact || this.outline.length > 0);
  }

  ariaLabel(exact: DataSource | null): string {
    return `source.name.${exact ?? 'unknown'}`;
  }

  toggle(event: Event) {
    this.pinned = !this.panel?.overlayVisible;
    this.open(event, !this.pinned);
  }

  hoverStart(event: Event) {
    const target = event.currentTarget as Element;
    this.hoverTimer = setTimeout(() => this.open(null, false, target), 250);
  }

  hoverEnd() {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    if (!this.pinned) {
      this.panel?.hide();
    }
  }

  closed() {
    this.pinned = false;
    this.changeDetector.markForCheck();
  }

  private open(event: Event | null, hide: boolean, target?: Element) {
    if (hide) {
      this.panel?.hide();
      return;
    }
    if (this.source?.overwriteId && !this.manual$) {
      this.manual$ = this.companyService
        .readOverwrites([this.source.overwriteId])
        .pipe(
          map((overwrites) => toManualChange(overwrites[0])),
          catchError(() => of(null)),
          shareReplay(1)
        );
    }
    this.panel?.show(event, target);
    this.changeDetector.markForCheck();
  }
}

const toManualChange = (
  overwrite: ApiOverwrite | undefined
): ManualChange | null =>
  overwrite
    ? {
        user: overwrite.users
          ? `${overwrite.users.first_name} ${overwrite.users.last_name}`.trim()
          : overwrite.overwrite_by,
        date: overwrite.updated_at || overwrite.created_at,
        original: overwrite.import_value,
      }
    : null;
